import React, { useState, useEffect } from 'react';
import { Container, Typography, Paper, Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Alert, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField } from '@mui/material';
import { collection, getDocs, deleteDoc, doc, addDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Edit as EditIcon, Delete as DeleteIcon, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import PriceManagement from './PriceManagement';
import { sortYachtsForDisplay } from '../utils/yachtSort';

const AdminDashboard = () => {
  const [yachts, setYachts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingYacht, setEditingYacht] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrls: [], // Keep imageUrls for backward compatibility, but now stores file names
    capacity: '',
    length: '',
    location: '',
    features: ''
  });
  const [imageFileName, setImageFileName] = useState('');
  const navigate = useNavigate();
  const { isAdmin, currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      navigate('/');
      return;
    }
    
    fetchYachts();
  }, [currentUser, isAdmin, navigate]);

  const fetchYachts = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'yachts'));
      const yachtsData = querySnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data()
      }));
      setYachts(sortYachtsForDisplay(yachtsData));
    } catch (error) {
      setError('Błąd podczas pobierania jachtów: ' + error.message);
    }
  };

  const persistYachtDisplayOrder = async (orderedYachts) => {
    await Promise.all(
      orderedYachts.map((y, index) =>
        updateDoc(doc(db, 'yachts', y.id), { displayOrder: index })
      )
    );
  };

  const handleReorderYacht = async (index, direction) => {
    const sorted = sortYachtsForDisplay(yachts);
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sorted.length) return;

    [sorted[index], sorted[targetIndex]] = [sorted[targetIndex], sorted[index]];

    try {
      await persistYachtDisplayOrder(sorted);
      await fetchYachts();
    } catch (error) {
      setError('Błąd zmiany kolejności: ' + error.message);
    }
  };

  const handleDeleteYacht = async (yachtId) => {
    if (window.confirm('Czy na pewno chcesz usunąć ten jacht?')) {
      try {
        await deleteDoc(doc(db, 'yachts', yachtId));
        await fetchYachts();
      } catch (error) {
        setError('Błąd podczas usuwania jachtu: ' + error.message);
      }
    }
  };

  const handleOpen = (yacht = null) => {
    if (yacht) {
      setEditingYacht(yacht);
      // Convert existing imageUrls to file names if they're full paths
      const imageNames = (yacht.imageUrls || []).map(url => {
        if (url.startsWith('/images/yachts/')) {
          return url.replace('/images/yachts/', '');
        }
        if (url.startsWith('http')) {
          // If it's a URL, extract filename or keep as is for migration
          return url;
        }
        return url;
      });
      setFormData({
        name: yacht.name || '',
        description: yacht.description || '',
        imageUrls: imageNames,
        capacity: yacht.capacity || '',
        length: yacht.length || '',
        location: yacht.location || '',
        features: yacht.features || ''
      });
    } else {
      setEditingYacht(null);
      setFormData({
        name: '',
        description: '',
        imageUrls: [],
        capacity: '',
        length: '',
        location: '',
        features: ''
      });
    }
    setImageFileName('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingYacht(null);
    setError('');
    setImageFileName('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddImageFileName = () => {
    if (imageFileName && !formData.imageUrls.includes(imageFileName)) {
      setFormData(prev => ({
        ...prev,
        imageUrls: [...prev.imageUrls, imageFileName]
      }));
      setImageFileName('');
    }
  };

  const handleRemoveImageUrl = (fileNameToRemove) => {
    setFormData(prev => ({
      ...prev,
      imageUrls: prev.imageUrls.filter(fileName => fileName !== fileNameToRemove)
    }));
  };

  const handleMoveImage = (index, direction) => {
    setFormData(prev => {
      const newImageUrls = [...prev.imageUrls];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      
      // Check if move is valid
      if (newIndex < 0 || newIndex >= newImageUrls.length) {
        return prev;
      }
      
      // Swap images
      [newImageUrls[index], newImageUrls[newIndex]] = [newImageUrls[newIndex], newImageUrls[index]];
      
      return {
        ...prev,
        imageUrls: newImageUrls
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const yachtData = {
        ...formData,
        capacity: Number(formData.capacity),
        length: Number(formData.length)
      };

      if (editingYacht) {
        await updateDoc(doc(db, 'yachts', editingYacht.id), yachtData);
      } else {
        const maxOrder = yachts.reduce(
          (max, y) =>
            Math.max(
              max,
              typeof y.displayOrder === 'number' && !Number.isNaN(y.displayOrder)
                ? y.displayOrder
                : -1
            ),
          -1
        );
        await addDoc(collection(db, 'yachts'), {
          ...yachtData,
          displayOrder: maxOrder + 1
        });
      }
      
      handleClose();
      await fetchYachts();
    } catch (error) {
      setError('Failed to save yacht: ' + error.message);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (!currentUser || !isAdmin) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Zarządzanie Jachtami" />
          <Tab label="Zarządzanie Cenami" />
          <Tab label="Pomoc" />
        </Tabs>
      </Box>

      {activeTab === 0 ? (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" component="h2">
              Jachty
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => handleOpen()}
            >
              Dodaj Nowy Jacht
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={120}>Kolejność</TableCell>
                  <TableCell>Nazwa</TableCell>
                  <TableCell>Pojemność</TableCell>
                  <TableCell>Akcje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {yachts.map((yacht, index) => (
                  <TableRow key={yacht.id}>
                    <TableCell>
                      <IconButton
                        size="small"
                        aria-label="Przenieś wyżej"
                        disabled={index === 0}
                        onClick={() => handleReorderYacht(index, 'up')}
                      >
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Przenieś niżej"
                        disabled={index === yachts.length - 1}
                        onClick={() => handleReorderYacht(index, 'down')}
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell>{yacht.name}</TableCell>
                    <TableCell>{yacht.capacity} osób</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleOpen(yacht)}
                        size="small"
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteYacht(yacht.id)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : activeTab === 1 ? (
        <Box sx={{ mb: 4 }}>
          {yachts.map((yacht) => (
            <PriceManagement key={yacht.id} yacht={yacht} />
          ))}
        </Box>
      ) : (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            🎯 Przewodnik Administratora
          </Typography>
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              📋 Zarządzanie Jachtami
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Dodaj Nowy Jacht:</strong> Kliknij przycisk "Dodaj Nowy Jacht" aby dodać nowy jacht do oferty
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Edytuj Jacht:</strong> Kliknij ikonę ołówka przy jachcie aby edytować jego dane
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Usuń Jacht:</strong> Kliknij ikonę kosza aby usunąć jacht z oferty
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Kolejność wyświetlania:</strong> Użyj strzałek w kolumnie „Kolejność”, aby zmienić kolejność jachtów na stronie głównej
            </Typography>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              💰 Zarządzanie Cenami
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Ustaw Ceny:</strong> Przejdź do zakładki "Zarządzanie Cenami"
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Wybierz Jacht:</strong> Każdy jacht ma swój własny kalendarz cen
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Kliknij na Datę:</strong> Kliknij na dowolną datę w kalendarzu aby ustawić cenę
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Nawigacja:</strong> Użyj strzałek aby przejść między miesiącami
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Usuń Cenę:</strong> Zostaw pole ceny puste aby usunąć cenę dla danej daty
            </Typography>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              📅 Kalendarz Dostępności
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Zielony znacznik ✓:</strong> Jacht dostępny w tej dacie
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Pomarańczowy znacznik ✗:</strong> Jacht zajęty w tej dacie
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Kliknij na Datę:</strong> Aby zmienić status dostępności (dostępny/zajęty)
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Wyświetlanie Cen:</strong> Ceny są wyświetlane na kalendarzu dla każdej daty
            </Typography>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              🔗 Linki Dostępu
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Panel Admina:</strong> <code>http://localhost:3000/admin</code>
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Logowanie Admina:</strong> <code>http://localhost:3000/admin-login</code>
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Strona Główna:</strong> <code>http://localhost:3000/</code>
            </Typography>
          </Box>
        </Paper>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingYacht ? 'Edytuj Jacht' : 'Dodaj Nowy Jacht'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label="Nazwa"
                  fullWidth
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Opis"
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Zdjęcia
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Umieść pliki zdjęć w folderze <code>public/images/yachts/</code> i wprowadź tutaj nazwy plików (np. <code>yacht1.jpg</code>)
                </Alert>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    label="Nazwa pliku zdjęcia"
                    placeholder="np. yacht1.jpg"
                    fullWidth
                    value={imageFileName}
                    onChange={(e) => setImageFileName(e.target.value)}
                    helperText="Nazwa pliku z folderu public/images/yachts/"
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddImageFileName}
                    disabled={!imageFileName}
                    sx={{ alignSelf: 'flex-start', mt: 1 }}
                  >
                    Dodaj
                  </Button>
                </Box>
                {formData.imageUrls.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {formData.imageUrls.map((fileName, index) => {
                      const imagePath = fileName.startsWith('http') || fileName.startsWith('/') 
                        ? fileName 
                        : `/images/yachts/${fileName}`;
                      return (
                        <Box 
                          key={index} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            p: 1.5, 
                            border: '1px solid #e0e0e0', 
                            borderRadius: 1,
                            backgroundColor: 'background.paper',
                            '&:hover': {
                              backgroundColor: 'action.hover'
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveImage(index, 'up')}
                              disabled={index === 0}
                              sx={{ 
                                width: 32, 
                                height: 32,
                                color: index === 0 ? 'action.disabled' : 'primary.main'
                              }}
                              title="Przenieś w górę"
                            >
                              <ArrowUpward fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveImage(index, 'down')}
                              disabled={index === formData.imageUrls.length - 1}
                              sx={{ 
                                width: 32, 
                                height: 32,
                                color: index === formData.imageUrls.length - 1 ? 'action.disabled' : 'primary.main'
                              }}
                              title="Przenieś w dół"
                            >
                              <ArrowDownward fontSize="small" />
                            </IconButton>
                          </Box>
                          <Box sx={{ 
                            position: 'relative',
                            width: 100, 
                            height: 60, 
                            borderRadius: 1,
                            overflow: 'hidden',
                            flexShrink: 0
                          }}>
                            <img 
                              src={imagePath} 
                              alt={`Preview ${index + 1}`} 
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover',
                                filter: 'blur(0.5px)',
                                imageRendering: 'smooth',
                                WebkitFontSmoothing: 'antialiased'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 4,
                                left: 4,
                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                color: 'white',
                                borderRadius: '50%',
                                width: 24,
                                height: 24,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.75rem',
                                fontWeight: 'bold'
                              }}
                            >
                              {index + 1}
                            </Box>
                          </Box>
                          <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-word' }}>
                            {fileName}
                          </Typography>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveImageUrl(fileName)}
                            title="Usuń zdjęcie"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="capacity"
                  label="Pojemność"
                  type="number"
                  fullWidth
                  value={formData.capacity}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="length"
                  label="Długość (m)"
                  type="number"
                  fullWidth
                  value={formData.length}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="location"
                  label="Lokalizacja"
                  fullWidth
                  value={formData.location}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="features"
                  label="Wyposażenie"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.features}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Anuluj</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingYacht ? 'Zapisz Zmiany' : 'Dodaj Jacht'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard; 